import { creating, exist, Id } from '@campaign-portal/namespace/common/id';
import { RateStatus, TrafficPurpose } from '@campaign-portal/namespace/common/enums';
import { CountryCode, NetworkCode } from '@campaign-portal/namespace/entities/mccmnc/specs';
import { Rate, RateHistoryEntity as IRateHistoryEntity } from '@campaign-portal/namespace/entities/rates/specs';
import { ANY_NETWORKS, REST_OF_WORLD_MCC } from '../../constants';

export interface RateEntityConfig<ID = exist | creating> extends Partial<Rate<ID>> {
	id: ID;
	currencyId: Id<exist>;
	activeFrom: string | null;
	subscriptionId: Id<exist>;
	country: CountryCode;
}

export class RateEntity<ID = exist | creating> implements Rate<ID> {
	[key: string]: unknown;

	id: ID;
	activeFrom: string | null;
	activeTill: string | null;
	price: number;
	subscriptionId: Id<exist>;
	currencyId: Id<exist>;
	country: CountryCode;
	network?: NetworkCode;
	parentId?: Id<exist>;
	history?: RateHistory[];
	closeRate?: boolean;
	trafficPurpose?: TrafficPurpose;
	senderName?: string;

	constructor(config: RateEntityConfig<ID>) {
		this.id = config.id;
		this.currencyId = config.currencyId;
		this.activeFrom = config.activeFrom;
		this.activeTill = config.activeTill ?? null;
		this.subscriptionId = config.subscriptionId;
		this.country = config.country;
		this.price = config.price ?? 0;
		this.network = config.network;
		this.parentId = config.parentId;
		this.history = config.history?.map((h) => new RateHistory(h));
		this.closeRate = config.closeRate;
		this.trafficPurpose = config.trafficPurpose;
		this.senderName = config.senderName;
	}

	isPlanned(): boolean {
		if (!this.activeFrom) {
			return false;
		}
		return new Date() < new Date(this.activeFrom);
	}

	isClosed(): boolean {
		if (!this.activeTill) {
			return false;
		}
		return new Date() > new Date(this.activeTill);
	}

	isAnyNetwork(): boolean {
		return this.network?.mnc === ANY_NETWORKS;
	}

	isRestOfWorld(): boolean {
		return this.country?.mcc === REST_OF_WORLD_MCC;
	}

	hasHistory(): boolean {
		return !!this.history?.length;
	}

	getStatus(): RateStatus {
		if (this.isPlanned()) {
			return RateStatus.PLANNED;
		}
		if (this.isClosed()) {
			return RateStatus.CLOSED;
		}
		return RateStatus.CURRENT;
	}
}

export interface RateHistoryConfig<ID = exist | creating> extends Partial<IRateHistoryEntity<ID>> {
	id: ID;
}

export class RateHistory<ID = exist | creating> implements IRateHistoryEntity<ID> {
	[key: string]: unknown;

	id: ID;
	activeFrom: string | null;
	activeTill: string | null;
	price: number;
	type: RateStatus;

	constructor(config: RateHistoryConfig<ID>) {
		this.id = config.id;
		this.activeFrom = config.activeFrom ?? null;
		this.activeTill = config.activeTill ?? null;
		this.price = config.price ?? 0;
		this.type = config.type ?? RateStatus.PLANNED;
	}

	isPlanned(): boolean {
		return this.type === RateStatus.PLANNED;
	}

	isClosed(): boolean {
		return this.type === RateStatus.CLOSED;
	}

	isCurrent(): boolean {
		return this.type === RateStatus.CURRENT;
	}
}
